import { useMutation } from "@tanstack/react-query";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import { changePassword, validateToken } from "../api/password.api";
import { ChangePasswordModel } from "../api/models/password.models";
import { UIState } from "../types/ui-types";
import { useNavigate } from "react-router";

const usePasswords = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bearerToken, toast, setTempToken, setTempUserId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.setTempToken,
      state.setTempUserId,
    ])
  );

  const validateUserToken = async (payload: {
    userId: number;
    token: string;
  }) => {
    const result = await validateTokenMutation.mutateAsync(payload);

    return result;
  };

  const validateTokenMutation = useMutation({
    mutationFn: (payload: { userId: number; token: string }) =>
      validateToken(payload.userId, payload.token),

    onSuccess: () => {
      // Token is valid
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (payload: ChangePasswordModel) =>
      changePassword(bearerToken as string, payload),
    onError: (error) => {
      if ((error as any)?.status === 400) {
        toast?.current?.show({
          severity: "error",
          detail: t("text-reset-password-token-expired"),
          life: 3000,
        });
        return;
      } else {
        console.error("Failed to change password:", error);
        toast?.current?.show({
          severity: "error",
          detail: t("text-password-change-failed"),
          life: 3000,
        });
      }
    },
    onSuccess: () => {
      setTempToken(undefined);
      setTempUserId(undefined);
      navigate("/login");
      toast?.current?.show({
        severity: "success",
        detail: t("text-password-changed"),
        life: 3000,
      });
    },
  });

  const changeUserPassword = async (payload: ChangePasswordModel) => {
    const result = await changePasswordMutation.mutateAsync(payload);

    return result;
  };

  return {
    changeUserPassword,
    isChangingPassword: changePasswordMutation.isPending,
    error: changePasswordMutation.error,
    validateUserToken,
    isValidatingToken: validateTokenMutation.isPending,
  };
};

export default usePasswords;
